"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import Image from "next/image";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import { Mic, PhoneOff, X, Loader2 } from "lucide-react";
import { useLandingLang } from "@/lib/landingI18n";
import { trackEvent } from "@/lib/analyticsClient";

const AGENT_ID = process.env.NEXT_PUBLIC_ELEVENLABS_AGENT_ID || "agent_0401kwmb49n6en78nyrc1axp08nh";
const CALL_LIMIT_SECONDS = 120;

type Stage = "idle" | "form" | "mic" | "connecting" | "active" | "ended" | "error";

interface LeadForm {
  name: string;
  phone: string;
  email: string;
  company: string;
  consent: boolean;
}

const EMPTY_FORM: LeadForm = { name: "", phone: "", email: "", company: "", consent: false };

function formatRemainingTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

function VoiceWidgetInner() {
  const { L, lang } = useLandingLang();
  const V = L.voiceWidget;

  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("idle");
  const [form, setForm] = useState<LeadForm>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; contact?: string; consent?: string }>({});
  const [statusMessage, setStatusMessage] = useState("");
  const [remainingSeconds, setRemainingSeconds] = useState(CALL_LIMIT_SECONDS);

  const orbOuterRef = useRef<HTMLDivElement>(null);
  const orbInnerRef = useRef<HTMLDivElement>(null);
  const callEndsAtRef = useRef<number | null>(null);
  const callLimitReachedRef = useRef(false);

  // Held open from the explicit permission check until the SDK's own session
  // is confirmed connected (or fails) — releasing it immediately after the
  // check caused a re-acquire race where the SDK's own getUserMedia call
  // could fail right after ours released the device.
  const micStreamRef = useRef<MediaStream | null>(null);

  function releaseMicStream() {
    micStreamRef.current?.getTracks().forEach((track) => track.stop());
    micStreamRef.current = null;
  }

  function stopCallSession(nextStage: Stage = "ended") {
    callEndsAtRef.current = null;
    callLimitReachedRef.current = false;
    setRemainingSeconds(CALL_LIMIT_SECONDS);
    conversation.endSession();
    releaseMicStream();
    setStage(nextStage);
  }

  const conversation = useConversation({
    // Self-hosted worklets (see public/worklets/) — the CSP here doesn't allow
    // blob:/data: script sources, which is what the SDK falls back to otherwise.
    workletPaths: {
      rawAudioProcessor: "/worklets/rawAudioProcessor.js",
      audioConcatProcessor: "/worklets/audioConcatProcessor.js",
    },
    onConnect: () => {
      releaseMicStream();
      callEndsAtRef.current = Date.now() + CALL_LIMIT_SECONDS * 1000;
      callLimitReachedRef.current = false;
      setRemainingSeconds(CALL_LIMIT_SECONDS);
      setStage("active");
      trackEvent("voice_start");
    },
    onDisconnect: (details) => {
      console.warn("[VoiceWidget] disconnected", details);
      trackEvent("voice_end");
      releaseMicStream();
      callEndsAtRef.current = null;
      setRemainingSeconds(CALL_LIMIT_SECONDS);
      setStage((s) => {
        if (callLimitReachedRef.current) {
          callLimitReachedRef.current = false;
          return "idle";
        }
        return s === "active" ? "ended" : s;
      });
    },
    onError: (message, context) => {
      console.error("[VoiceWidget] ElevenLabs error:", message, context);
      releaseMicStream();
      callEndsAtRef.current = null;
      callLimitReachedRef.current = false;
      setRemainingSeconds(CALL_LIMIT_SECONDS);
      setStatusMessage(V.connectError);
      setStage("error");
    },
  });

  // Audio-reactive halo — direct style writes (not React state) so the loop
  // can run every animation frame without triggering re-renders.
  useEffect(() => {
    if (stage !== "active") return;
    let raf = 0;
    const loop = () => {
      const inVol = conversation.getInputVolume?.() ?? 0;
      const outVol = conversation.getOutputVolume?.() ?? 0;
      const vol = Math.min(Math.max(inVol, outVol), 1);
      const scale = 1 + vol * 0.4;
      const opacity = 0.55 + vol * 0.45;
      if (orbOuterRef.current) {
        orbOuterRef.current.style.transform = `scale(${scale})`;
        orbOuterRef.current.style.opacity = String(opacity);
      }
      if (orbInnerRef.current) {
        orbInnerRef.current.style.transform = `scale(${1 + vol * 0.25})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [stage, conversation]);

  useEffect(() => {
    if (stage !== "active") {
      return;
    }

    const tick = () => {
      const endsAt = callEndsAtRef.current;
      if (!endsAt) {
        setRemainingSeconds(CALL_LIMIT_SECONDS);
        return;
      }

      const nextRemaining = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      setRemainingSeconds(nextRemaining);

      if (nextRemaining === 0 && !callLimitReachedRef.current) {
        callLimitReachedRef.current = true;
        callEndsAtRef.current = null;
        conversation.endSession();
        releaseMicStream();
        setOpen(false);
        setStage("idle");
      }
    };

    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [stage, conversation]);

  function updateField<K extends keyof LeadForm>(key: K, value: LeadForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function validate(): boolean {
    const errors: typeof fieldErrors = {};
    if (!form.name.trim()) errors.name = V.errorName;
    if (!form.phone.trim() && !form.email.trim()) errors.contact = V.errorContact;
    if (!form.consent) errors.consent = V.errorConsent;
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setStatusMessage("");
    setStage("mic");

    // 1. Save the lead (best-effort — a failure here shouldn't block the call).
    let leadId = "";
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang, source: "voice" }),
      });
      const data = await res.json();
      leadId = typeof data?.id === "string" ? data.id : "";
      trackEvent("lead_submit", { source: "voice" });
    } catch {
      // ignore — proceed without a persisted lead id
    }

    // 2. Explicit microphone permission before the call starts. The stream is
    // kept open (not stopped) until the session actually connects — see
    // releaseMicStream() — to avoid a re-acquire race with the SDK's own
    // getUserMedia call moments later.
    try {
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setStatusMessage(V.micDeniedError);
      setStage("error");
      return;
    }

    // 3. Connect to ElevenLabs — prefer a signed URL (private agent) and fall
    // back to the public agent id if no server-side API key is configured.
    setStage("connecting");
    let signedUrl: string | null = null;
    try {
      const res = await fetch(`/api/elevenlabs/signed-url?agentId=${encodeURIComponent(AGENT_ID)}`);
      const data = await res.json();
      signedUrl = typeof data?.signedUrl === "string" ? data.signedUrl : null;
    } catch {
      // ignore — fall back to the public agent id
    }

    const dynamicVariables = {
      user_name: form.name.trim(),
      user_phone: form.phone.trim(),
      user_email: form.email.trim(),
      user_company: form.company.trim(),
      lead_id: leadId,
    };

    try {
      if (signedUrl) {
        conversation.startSession({ signedUrl, dynamicVariables });
      } else {
        conversation.startSession({ agentId: AGENT_ID, dynamicVariables });
      }
    } catch {
      setStatusMessage(V.connectError);
      setStage("error");
    }
  }

  function handleEnd() {
    stopCallSession("ended");
  }

  function reset() {
    releaseMicStream();
    callEndsAtRef.current = null;
    callLimitReachedRef.current = false;
    setRemainingSeconds(CALL_LIMIT_SECONDS);
    setStage("idle");
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setStatusMessage("");
  }

  function closePopup() {
    if (stage === "active") {
      stopCallSession("ended");
    }
    setOpen(false);
    if (stage !== "active") reset();
  }

  const statusText = (() => {
    switch (stage) {
      case "idle":
      case "form":
        return V.readyStatus;
      case "mic":
        return V.requestingMic;
      case "connecting":
        return V.connecting;
      case "active":
        return conversation.isSpeaking ? V.activeSpeaking : V.activeListening;
      case "ended":
        return V.endedMessage;
      case "error":
        return statusMessage || V.genericError;
    }
  })();

  return (
    <>
      {/* Launcher — sits above the text-chat button */}
      <button
        onClick={() => setOpen((o) => {
          if (!o) trackEvent("voice_widget_open");
          return !o;
        })}
        aria-label={V.startCta}
        className="fixed bottom-24 right-5 z-50 flex h-[61px] w-[61px] items-center justify-center overflow-hidden rounded-full ring-2 ring-[var(--af-accent)] shadow-lg transition hover:scale-105"
      >
        {open ? (
          <span className="flex h-full w-full items-center justify-center bg-[var(--af-bg)] text-[var(--af-text)]">
            <X size={22} />
          </span>
        ) : (
          <span className="relative h-full w-full">
            {stage === "active" ? (
              <Image src="/avatars/ariun.png" alt={V.name} fill sizes="61px" className="object-cover object-top" />
            ) : (
              <video
                src="/avatars/ariun-idle.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="h-full w-full object-cover object-top"
              />
            )}
            {stage === "active" && (
              <>
                <span className="absolute inset-0 rounded-full ring-2 ring-[#4ade80] af-pulse-dot" />
                <span className="absolute -left-3 -top-2 rounded-full border border-[var(--af-border)] bg-[var(--af-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--af-text)] shadow-sm">
                  {formatRemainingTime(remainingSeconds)}
                </span>
              </>
            )}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closePopup}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full min-w-0 max-w-md rounded-[1.75rem] border border-[var(--af-border)] bg-[var(--af-bg)] p-6 text-center shadow-2xl sm:p-8"
          >
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={closePopup}
                aria-label={V.cancelCta}
                className="-mt-2 -mr-2 flex h-8 w-8 items-center justify-center rounded-full text-[var(--af-muted)] transition hover:text-[var(--af-text)]"
              >
                <X size={18} />
              </button>
            </div>

            {stage !== "form" && (
              <>
                <div className="relative mx-auto -mt-2 h-28 w-28">
                  {stage === "active" && (
                    <>
                      <div
                        ref={orbOuterRef}
                        className="af-orb-spin absolute -inset-5 rounded-full blur-md"
                        style={{ background: "conic-gradient(from 0deg, #ff8a00, #ffd28a, #ffffff, #ff8a00)" }}
                      />
                      <div
                        ref={orbInnerRef}
                        className="af-orb-spin-reverse absolute -inset-2.5 rounded-full opacity-70 blur-sm"
                        style={{ background: "conic-gradient(from 90deg, #ffffff, #ff8a00, #ffb37a, #ffffff)" }}
                      />
                    </>
                  )}
                  <div className="af-breathe relative h-full w-full overflow-hidden rounded-full ring-4 ring-[var(--af-bg-soft)]">
                    {stage === "active" ? (
                      <Image src="/avatars/ariun.png" alt={V.name} fill sizes="112px" className="object-cover object-top" />
                    ) : (
                      <video
                        src="/avatars/ariun-idle.mp4"
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="h-full w-full object-cover object-top"
                      />
                    )}
                  </div>
                  <span
                    className={`absolute bottom-1 right-1 h-4 w-4 rounded-full ring-4 ring-[var(--af-bg)] ${
                      stage === "active" ? "af-pulse-dot bg-[#4ade80]" : "bg-[var(--af-muted)]"
                    }`}
                  />
                </div>

                <h3 className="mt-4 text-lg font-semibold text-[var(--af-text)]">{V.name}</h3>
                <p className="text-[13px] text-[var(--af-muted)]">{V.role}</p>
                <p className="mt-2 min-h-[20px] text-[13px] text-[var(--af-muted)]">{statusText}</p>
                {stage === "active" && (
                  <div className="mt-3 inline-flex min-w-20 items-center justify-center rounded-full border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-3 py-1 text-sm font-semibold tabular-nums text-[var(--af-text)]">
                    {formatRemainingTime(remainingSeconds)}
                  </div>
                )}

                <div className="mt-6">
                  {stage === "idle" && (
                    <button
                      onClick={() => setStage("form")}
                      className="inline-flex items-center gap-2 rounded-full bg-[var(--af-accent)] px-7 py-3.5 text-base font-semibold text-black transition hover:bg-[var(--af-accent-hover)]"
                    >
                      <Mic size={18} />
                      {V.startCta}
                    </button>
                  )}
                  {(stage === "mic" || stage === "connecting") && (
                    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--af-border)] px-7 py-3.5 text-base font-medium text-[var(--af-muted)]">
                      <Loader2 size={18} className="animate-spin" />
                      {stage === "mic" ? V.requestingMic : V.connecting}
                    </div>
                  )}
                  {stage === "active" && (
                    <button
                      onClick={handleEnd}
                      className="inline-flex items-center gap-2 rounded-full border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-7 py-3.5 text-base font-semibold text-[var(--af-text)] transition hover:border-[var(--af-accent)]"
                    >
                      <PhoneOff size={18} />
                      {V.endCta}
                    </button>
                  )}
                  {(stage === "ended" || stage === "error") && (
                    <button
                      onClick={reset}
                      className="inline-flex items-center gap-2 rounded-full bg-[var(--af-accent)] px-7 py-3.5 text-base font-semibold text-black transition hover:bg-[var(--af-accent-hover)]"
                    >
                      <Mic size={18} />
                      {stage === "error" ? V.tryAgain : V.restartCta}
                    </button>
                  )}
                </div>
              </>
            )}

            {stage === "form" && (
              <form onSubmit={handleSubmit} className="text-left">
                <h4 className="text-lg font-semibold text-[var(--af-text)]">{V.formTitle}</h4>

                <div className="mt-5 space-y-4">
                  <div>
                    <label htmlFor="vw-name" className="mb-1.5 block text-[13px] font-medium text-[var(--af-text)]">
                      {V.nameLabel}
                    </label>
                    <input
                      id="vw-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder={V.namePlaceholder}
                      aria-invalid={!!fieldErrors.name}
                      className="w-full rounded-xl border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-3.5 py-2.5 text-sm text-[var(--af-text)] outline-none placeholder:text-[var(--af-muted)] focus:border-[var(--af-accent)]"
                    />
                    {fieldErrors.name && <p className="mt-1 text-[12px] text-[#ef4444]">{fieldErrors.name}</p>}
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="vw-phone" className="mb-1.5 block text-[13px] font-medium text-[var(--af-text)]">
                        {V.phoneLabel}
                      </label>
                      <input
                        id="vw-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => updateField("phone", e.target.value)}
                        aria-invalid={!!fieldErrors.contact}
                        className="w-full rounded-xl border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-3.5 py-2.5 text-sm text-[var(--af-text)] outline-none focus:border-[var(--af-accent)]"
                      />
                    </div>
                    <div>
                      <label htmlFor="vw-email" className="mb-1.5 block text-[13px] font-medium text-[var(--af-text)]">
                        {V.emailLabel}
                      </label>
                      <input
                        id="vw-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        aria-invalid={!!fieldErrors.contact}
                        className="w-full rounded-xl border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-3.5 py-2.5 text-sm text-[var(--af-text)] outline-none focus:border-[var(--af-accent)]"
                      />
                    </div>
                  </div>
                  <p className="-mt-2 text-[12px] text-[var(--af-muted)]">{V.contactHelper}</p>
                  {fieldErrors.contact && <p className="-mt-2 text-[12px] text-[#ef4444]">{fieldErrors.contact}</p>}

                  <div>
                    <label htmlFor="vw-company" className="mb-1.5 block text-[13px] font-medium text-[var(--af-text)]">
                      {V.companyLabel}
                    </label>
                    <input
                      id="vw-company"
                      type="text"
                      value={form.company}
                      onChange={(e) => updateField("company", e.target.value)}
                      placeholder={V.companyPlaceholder}
                      className="w-full rounded-xl border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-3.5 py-2.5 text-sm text-[var(--af-text)] outline-none placeholder:text-[var(--af-muted)] focus:border-[var(--af-accent)]"
                    />
                  </div>

                  <div>
                    <label className="flex items-start gap-2.5 text-[13px] text-[var(--af-text)]">
                      <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={(e) => updateField("consent", e.target.checked)}
                        aria-invalid={!!fieldErrors.consent}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--af-border)] accent-[var(--af-accent)]"
                      />
                      {V.consentLabel}
                    </label>
                    {fieldErrors.consent && <p className="mt-1 text-[12px] text-[#ef4444]">{fieldErrors.consent}</p>}
                  </div>
                </div>

                <button
                  type="submit"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--af-accent)] px-7 py-3.5 text-base font-semibold text-black transition hover:bg-[var(--af-accent-hover)]"
                >
                  <Mic size={18} />
                  {V.submitCta}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function VoiceWidget() {
  return (
    <ConversationProvider>
      <VoiceWidgetInner />
    </ConversationProvider>
  );
}
